import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, ArrowLeft, Calendar, MessageCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import CtaBanner from "@/components/CtaBanner";
import { BLOG_POSTS } from "@/lib/blogData";
import { CONTACT } from "@/lib/company";
import usePageMeta from "@/hooks/usePageMeta";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  usePageMeta({
    title: post ? `${post.title} · Great Solutions US` : "Artículo · Great Solutions US",
    description: post?.excerpt,
  });

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post">
      <Navbar />
      <main className="pt-32 pb-20">
        <article className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0B2D5C] mb-8">
            <ArrowLeft size={16} />
            Volver al blog
          </Link>

          <div className="flex items-center gap-3 mb-5">
            <span className="text-[10px] font-bold text-[#0B2D5C] bg-[#0B2D5C]/10 px-2 py-1 rounded-sm tracking-wider uppercase">
              {post.category}
            </span>
            <span className="text-xs text-[#475569] flex items-center gap-1">
              <Clock size={12} /> {post.readTime}
            </span>
            <span className="text-xs text-[#475569] flex items-center gap-1">
              <Calendar size={12} /> {post.date}
            </span>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0f172a] leading-tight mb-6">
            {post.title}
          </h1>

          <p className="text-lg text-[#475569] leading-relaxed mb-8">{post.excerpt}</p>

          <div className="aspect-[16/9] overflow-hidden rounded-sm border border-[#e2e8f0] mb-10">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>

          <div className="prose max-w-none">
            {post.content.map((section, i) => (
              <div key={i} className="mb-8">
                <h2 className="font-display text-2xl font-bold text-[#0f172a] mb-3">{section.h}</h2>
                <p className="text-[#334155] leading-relaxed text-base">{section.p}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 border border-[#e2e8f0] rounded-sm p-6 bg-[#f8fafc]">
            <div className="text-xs font-semibold text-[#0B2D5C] tracking-[0.2em] uppercase mb-2">
              ¿Necesitas ayuda?
            </div>
            <h3 className="font-display text-xl font-bold text-[#0f172a] mb-4">
              Habla con Yohaly directamente
            </h3>
            <div className="flex flex-wrap gap-2">
              <a
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-medium px-5 py-2.5 rounded-sm text-sm transition-colors"
              >
                <MessageCircle size={16} />
                WhatsApp
              </a>
              <Link
                to="/#contacto"
                className="inline-flex items-center gap-2 bg-[#0B2D5C] hover:bg-[#081F42] text-white font-medium px-5 py-2.5 rounded-sm text-sm transition-colors"
              >
                Agenda una consulta
              </Link>
            </div>
          </div>
        </article>
      </main>
      <CtaBanner />
      <Footer />
      <FloatingButtons />
    </div>
  );
}
